import { groupBySection, packColumns, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent } from '../types.js';
import { discountPercent } from '../format.js';
import { featuredItem } from './cafe-panels.js';
import { badge, bilingual, description, fitScale, price, variantLabel, type MenuTemplate, type RenderContext } from './common.js';
import {
  CC_FAMILY,
  CC_THEME,
  CC_VARIANTS,
  ccBlocks,
  ccColumn,
  ccDeco,
  ccDecoCss,
  ccHeader,
  ccHeaderCss,
  ccHeaderHeight,
  ccPriceCircle,
  ccPriceCircleCss,
  ccRing,
  ccRingCss,
  ccRootCss,
  ccRowsCss,
} from './chalk-chili-trio.js';

/**
 * Chalk & Chili, layout B: the featured dish as a huge round photo in a red ring, cut off by the
 * corner of the screen, with its price in a big red circle and its name in script; the board's
 * title and the rest of the menu in two columns beside it.
 */

const S = '.mb-t-chalk-chili-hero';
const TITLE_EM = 4.6;
/** Height of the hero band in portrait. */
const PORTRAIT_HERO = 36;

function columns(board: MenuBoardContent, portrait: boolean, hero: boolean): MenuColumn[] {
  const header = ccHeaderHeight(board, TITLE_EM) + 1.8;
  const capacity = portrait ? 120 - 5 - header - (hero ? PORTRAIT_HERO + 1.5 : 0) : 67.5 - 5 - header;
  const perLine = portrait ? 44 : hero ? 40 : 46;
  return [ccColumn(board, capacity, perLine), ccColumn(board, capacity, perLine)];
}

function heroText(ctx: RenderContext, item: MenuItemContent): string {
  const sizes = item.variants.length
    ? `<p class="cc2-hsizes">${item.variants
        .map((v) => `<span><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <b dir="ltr">${price(ctx, v.price)}</b></span>`)
        .join('')}</p>`
    : discountPercent(item) !== null
      ? `<p class="cc2-hsizes"><s dir="ltr">${price(ctx, item.price)}</s></p>`
      : '';
  const badges = badge(ctx, item, 'cc2-hbadge');
  return [
    '<div class="cc2-htext">',
    badges ? `<div class="cc2-hbadges">${badges}</div>` : '',
    `<div class="cc2-hnames" style="font-size:${fitScale(item.title, 18, 0.6).toFixed(3)}em">${bilingual(ctx, item.title, item.titleAr, 'cc2-hname')}</div>`,
    sizes,
    description(ctx, item, 'cc2-hdesc'),
    '</div>',
  ].join('');
}

function hero(ctx: RenderContext, item: MenuItemContent): string {
  const circle = item.variants.length ? '' : ccPriceCircle(ctx, item.discountPrice ?? item.price, 'cc2-pc');
  return [
    '<div class="cc2-hero">',
    '<div class="cc2-stage">',
    ccRing(ctx, item.imageMediaId, 'cc2-ring'),
    circle,
    ccDeco('leaf', 'cc2-d1'),
    ccDeco('tomato', 'cc2-d2'),
    ccDeco('chili', 'cc2-d3'),
    '</div>',
    heroText(ctx, item),
    '</div>',
  ].join('');
}

export const chalkChiliHero: MenuTemplate = {
  id: 'chalk-chili-hero',
  name: 'Chalk & Chili · Hero and list',
  description:
    'The featured dish as a huge round photo in a red ring, cut off by the corner of the screen, with its price in a big red circle; the title and the rest of the menu in two chalkboard columns beside it.',
  defaultTheme: CC_THEME,
  variants: CC_VARIANTS,
  cuisines: ['pizza', 'pasta', 'burgers'],
  family: { ...CC_FAMILY, layout: 'Hero and list' },
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const featured = featuredItem(board);
    return packColumns(groupBySection(board, featured?.id), columns(board, orientation === 'portrait', Boolean(featured)), {
      balance: true,
    });
  },

  render(ctx) {
    const item = featuredItem(ctx.board);
    const lists = ctx.page.columns.map((c) => `<div class="cc2-col">${ccBlocks(ctx, c)}</div>`).join('');
    return [
      `<div class="cc2-grid${item ? '' : ' cc2-no-hero'}">`,
      item ? hero(ctx, item) : '',
      ccHeader(ctx, 'cc2-header', ctx.orientation === 'portrait' ? 16 : 18),
      `<div class="cc2-cols">${lists}</div>`,
      '</div>',
    ].join('');
  },

  css: `
${ccRootCss(S)}
${S}{padding:2.5em 3em}
${ccDecoCss(S)}
${ccRingCss(S)}
${ccRowsCss(S)}
${ccHeaderCss(S, 'cc2-header', TITLE_EM)}
${ccPriceCircleCss(S, 'cc2-pc', 8.5)}
${S} .cc2-grid{flex:1;min-height:0;display:grid;grid-template-columns:minmax(0,43fr) minmax(0,57fr);grid-template-rows:auto 1fr;
  grid-template-areas:"hero head" "hero list";column-gap:3em}
${S} .cc2-no-hero{grid-template-columns:minmax(0,1fr);grid-template-areas:"head" "list"}
${S}.mb-portrait .cc2-grid{grid-template-columns:minmax(0,1fr);grid-template-rows:auto auto 1fr;grid-template-areas:"head" "hero" "list"}
${S}.mb-portrait .cc2-no-hero{grid-template-rows:auto 1fr;grid-template-areas:"head" "list"}
${S} .cc2-header{grid-area:head;text-align:center;margin-bottom:1.8em}
${S} .cc2-cols{grid-area:list;min-height:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:3em}
${S} .cc2-col{min-width:0;display:flex;flex-direction:column}

${S} .cc2-hero{grid-area:hero;position:relative;min-height:0;display:flex;flex-direction:column;align-items:center}
${S}.mb-portrait .cc2-hero{flex-direction:row;align-items:center;gap:3em;height:${PORTRAIT_HERO}em;margin-bottom:1.5em}
${S} .cc2-stage{position:relative;flex:0 0 auto;width:46em;height:46em;margin-top:-9em;margin-inline-start:-10em;align-self:flex-start}
${S}.mb-portrait .cc2-stage{width:36em;height:36em;margin:0;margin-inline-start:-6em;align-self:center}
${S} .cc2-ring{width:100%;height:100%}
${S} .cc2-pc{position:absolute;z-index:2;bottom:3em;right:0;transform:rotate(-8deg)}
${S}[dir=rtl] .cc2-pc{right:auto;left:0}
${S}.mb-portrait .cc2-pc{bottom:1em}
${S} .cc2-d1{width:3.4em;height:3.4em;top:40%;right:-4.5em;transform:rotate(25deg)}
${S} .cc2-d2{width:3em;height:3em;bottom:12em;right:-4em}
${S} .cc2-d3{width:2.8em;height:2.8em;bottom:-1.5em;right:22%}
${S}[dir=rtl] .cc2-d1{right:auto;left:-4.5em}
${S}[dir=rtl] .cc2-d2{right:auto;left:-4em}
${S}[dir=rtl] .cc2-d3{right:auto;left:22%}
${S}.mb-portrait .cc2-d3{bottom:-1em}
${S} .cc2-htext{position:relative;z-index:1;display:flex;flex-direction:column;align-items:center;text-align:center;max-width:100%;margin-top:2.2em}
${S}.mb-portrait .cc2-htext{flex:1 1 auto;min-width:0;margin-top:0}
${S} .cc2-hbadges{display:flex;gap:.4em;margin-bottom:.4em}
${S} .cc2-hbadge{font-size:1.1em;font-weight:800;padding:.2em .8em;border-radius:99em;background:var(--mb-accent);color:#fff;white-space:nowrap}
${S} .cc2-hnames{max-width:100%}
${S} .cc2-hname{font-family:var(--mb-font-script);color:var(--mb-primary);font-size:4em;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .cc2-hname.mb-2nd{font-size:2.4em;color:var(--mb-accent)}
${S} .cc2-hsizes{display:flex;flex-wrap:wrap;justify-content:center;gap:.3em 1.2em;font-size:1.6em;color:var(--mb-accent);margin-top:.2em}
${S} .cc2-hsizes span{white-space:nowrap}
${S} .cc2-hsizes small{font-size:.75em;color:var(--mb-text);opacity:.8}
${S} .cc2-hsizes s{color:var(--mb-text);opacity:.7}
${S} .cc2-hdesc{font-size:1.25em;line-height:1.35;opacity:.85;max-width:30em;margin-top:.5em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .cc2-hdesc.mb-2nd{margin-top:.2em}
`,
};
